/* eslint-disable no-case-declarations */
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
// Store
import { Store } from '@ngxs/store';
import { GlobalConfigurationEnumsState } from '../../../state/global-configuration-enums.state';
import { SetIntegrationBackUrl, SetIntegrationOrganization, SetIntegrationOrganizationMenu } from '../../state/actions/generic-integation.actions';
// Service
import { BroadcastEventModel, BroadcastService, ObjectUtils, SystemApiService } from '@cv-invision/core';
// Models
import { IntegrationsUrlParamsModel } from '../../models/integrations-url-params.model';
import {
	IntegrationDocumentTypeColumns,
	IntegrationMappersExpandColumns,
	IntegrationOrganizationColumns,
	IntegrationProductTypeColumns,
	IntegrationInboundOrderStatusColumns,
	IntegrationOnHoldClientAccessColumns,
	IntegrationMessagesToIgnoreColumns,
	IntegrationInboundMessageColumns,
	IntegrationMappersSimplifiedColumns,
	IntegrationMappersUAD36Columns
} from '../../models/integration-columns.model';
import { EditableTableActions } from '../common/editable-table/model/editable-table.model';
import { IdNameObjectType } from "../../../state/models/global-configuration-state.model";

@Component({
	selector: 'cv-invision-ux-table-like',
	templateUrl: './table-like.component.html',
	styleUrls: ['./table-like.component.scss']
})
export class TableLikeComponent implements OnInit, OnDestroy {

	params = new IntegrationsUrlParamsModel();
	model: any = null;
	label = '';
	extraData: any = {};
	public formBuilderConfig: any = null;

	private atComponentDestroy: Subject<any> = new Subject<any>();

	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private store: Store,
		private broadcast: BroadcastService,
		private systemApiService: SystemApiService
	) { }

	ngOnInit(): void {
		const trackingSystemList: Array<IdNameObjectType> = this.store.selectSnapshot(GlobalConfigurationEnumsState.getTrackingSystems);

		this.route.params
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(params => {
				this.params = new IntegrationsUrlParamsModel();
				this.model = null;
				this.formBuilderConfig = null;
				this.extraData = {
					editInModal: true
				};
				this.params = Object.assign(this.params, params);
				let backUrl = '/global-configuration/generic-integrations';

				if ('mapperType' in params) {
					this.params.isMapper = true;
					this.params.section = params.mapperType;
				}

				switch (this.params.section) {
					case 'product-types':
						this.model = IntegrationProductTypeColumns;
						this.label = 'Product Types';
						break;
					case 'document-types':
						this.model = IntegrationDocumentTypeColumns;
						this.label = 'Document Types';

						const documentTypesTrackingSystemList = ObjectUtils.recursiveDeepCopy(trackingSystemList);
						documentTypesTrackingSystemList.push({ name: 'ClassValuation', id: 'ClassValuation' });
						this.loadTrackingSystemOptions(documentTypesTrackingSystemList, 'documentTypes');

						break;
					case 'organizations':
						this.model = IntegrationOrganizationColumns;
						this.label = 'Organizations';
						this.extraData = Object.assign(this.extraData, { showSelect: true, editInModal: true, saveAsObject: true });
						this.extraData.rootUrl = this.systemApiService.api.identitySystemService;
						break;
					case 'status-messages':
						this.model = IntegrationInboundOrderStatusColumns;
						this.label = 'Inbound Order Status';
						this.loadStatusMessagesOptions();
						break;
					case 'onhold-client-access':
						this.model = IntegrationOnHoldClientAccessColumns;
						this.label = 'On Hold Client Access';
						this.loadTrackingSystemList();
						this.loadCMPOnHoldReasonsOptions();
						this.loadReceiveFromClientOptions();
						break;
					case 'messages-to-ignore':
						this.model = IntegrationMessagesToIgnoreColumns;
						this.label = 'Outbound Messages';
						this.loadTrackingSystemList();
						break;
					case 'inbound-messages':
						this.model = IntegrationInboundMessageColumns;
						this.label = 'Inbound Messages';
						this.loadTrackingSystemList();
						this.loadHandleModeList();
						break;
				}

				if (this.params.isMapper) {
					const mapperTypes = {
						expanded: [
							'PropertyTypes',
							'LoanTypes',
							'LoanReasons',
							'OccupancyTypes'
						],
						custom: [
							'UAD36'
						]
					};

					let mapperModel: any = IntegrationMappersSimplifiedColumns;
					if (mapperTypes.expanded.indexOf(this.params.section) > -1) {
						mapperModel = IntegrationMappersExpandColumns;
					} else if (mapperTypes.custom.indexOf(this.params.section) > -1) {
						mapperModel = IntegrationMappersUAD36Columns;
					}

					this.model = { ...this.model, ...mapperModel };
					this.formBuilderConfig = { ...mapperModel.formBuilderConfig };

					this.label = this.params.section;
					const type = this.params.section.charAt(0).toLocaleLowerCase() + this.params.section.slice(1);

					const documentTypesTrackingSystemList = ObjectUtils.recursiveDeepCopy(trackingSystemList);
					this.loadTrackingSystemOptions(documentTypesTrackingSystemList, type);

					if (mapperTypes.custom.indexOf(this.params.section) > -1) {
						this.loadUAD36FieldList();
					}
				} else {
					this.formBuilderConfig = { ...this.model.formBuilderConfig };
				}

				if ('organizationIntegrationId' in params) {
					backUrl = `/global-configuration/generic-integration/${params.integrationId}/organizations`;
					this.params.parentIntegrationId = this.params.integrationId;
					this.params.integrationId = params.organizationIntegrationId;
				}

				this.store.dispatch(new SetIntegrationBackUrl(backUrl));
			});

		this.broadcast.onEvent(EditableTableActions.TABLE_ROW_ACTION)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(async (e: BroadcastEventModel) => {
				const row = e.event.row;
				let url = `/global-configuration/generic-integration/${this.params.integrationId}`;

				switch (e.event.type) {
					case 'goTo':
						this.store.dispatch(new SetIntegrationBackUrl(`${url}/organizations`));
						url = `${url}/organization/${row.integrationId}/product-types`;

						await this.store.dispatch(new SetIntegrationOrganization(row)).toPromise();
						await this.store.dispatch(new SetIntegrationOrganizationMenu()).toPromise();
						break;
				}

				this.router.navigate([url]);
			});
	}

	loadTrackingSystemOptions(trackingSystemList: IdNameObjectType[], mapperType?: string): void {
		if (trackingSystemList.length > 0) {
			this.model['formBuilderConfig']['trackingSystem']['options'] = trackingSystemList?.map((item: IdNameObjectType) => {
				return {
					label: item.name,
					value: item.id,
				};
			});

			if (this.params.section !== 'OnHoldReason' && this.params.section !== 'OrderStatus'){
				trackingSystemList.forEach((trackingSystem: IdNameObjectType) => {
					const trackingSystemStoreName = trackingSystem.id[0].toLowerCase() + trackingSystem.id.substring(1);
					try {
						const mapperTypesList = this.store.selectSnapshot((state: any) => ObjectUtils.getStateFromString(state, `globalConfiguration.${trackingSystemStoreName}.${mapperType}`));
						const mapperTypesOptions = mapperTypesList.map((item: any) => {
							return {
								label: item.name,
								value: item.id,
							};
						});

						const productsData = {}
						productsData[trackingSystemStoreName] = mapperTypesOptions;

						this.extraData = Object.assign(this.extraData, productsData);
					} catch (error) {
						// silence
					}

				})
			}

		}
	}

	/**
	 * Load status messages options
	 */
	private loadStatusMessagesOptions(): void {
		const actionList: Array<IdNameObjectType> = this.store.selectSnapshot(GlobalConfigurationEnumsState.getActionOptions);

		if (actionList.length > 0) {
			this.model['formBuilderConfig']['action']['options'] = actionList?.map((item: IdNameObjectType) => {
				return {
					label: item.name,
					value: item.id,
				};
			});
		}
	}

	/**
	 * Load CMP On Hold Reasons options
	 */
	private loadCMPOnHoldReasonsOptions(): void {
		const CMPOnHoldReasonsList: Array<IdNameObjectType> = this.store.selectSnapshot(GlobalConfigurationEnumsState.getCMPOnHoldReasonsOptions);

		if (CMPOnHoldReasonsList.length > 0) {
			this.model['formBuilderConfig']['holdReasonId']['options'] = CMPOnHoldReasonsList?.map((item: IdNameObjectType) => {
				return {
					label: item.name,
					value: item.id,
				};
			});
		}
	}

	/**
	 * Load receive from client options
	 */
	private loadReceiveFromClientOptions(): void {
		const receiveFromClientList: Array<IdNameObjectType> = this.store.selectSnapshot(GlobalConfigurationEnumsState.getReceiveFromClientOptions);

		if (receiveFromClientList.length > 0) {
			this.model['formBuilderConfig']['receiveFromClient']['options'] = receiveFromClientList?.map((item: IdNameObjectType) => {
				return {
					label: item.name,
					value: item.id,
				};
			});
		}
	}

	/**
	 * Load the tracking system list from client options
	 */
	private loadTrackingSystemList(): void {
		const trackingSystemList: Array<IdNameObjectType> = this.store.selectSnapshot(GlobalConfigurationEnumsState.getTrackingSystems);
		this.model['formBuilderConfig']['trackingSystem']['options'] = trackingSystemList.map((item: IdNameObjectType) => {
			return {
				label: item.name,
				value: item.id,
			};
		});
	}

	/**
	 * Load the handle mode list from client options
	 */
	private loadHandleModeList(): void {
		const handleModeList: Array<string> = this.store.selectSnapshot(GlobalConfigurationEnumsState.gethandleMode);
		this.model['formBuilderConfig']['handleMode']['options'] = handleModeList.map((item: string) => {
			return {
				label: item,
				value: item,
			};
		});
	}

	/**
	 * Load the UAD36 Field list
	 */
	private loadUAD36FieldList(): void {
		const fieldTypesList: Array<IdNameObjectType> = this.store.selectSnapshot(GlobalConfigurationEnumsState.getUAD36FieldTypes);
		this.model['formBuilderConfig']['field']['options'] = fieldTypesList.map((item: IdNameObjectType) => {
			return {
				label: item.name,
				value: item.id,
			};
		});
	}

	ngOnDestroy(): void {
		this.atComponentDestroy.next(true);
		this.atComponentDestroy.unsubscribe();
	}

}
